import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useGet } from "../../actprep-ui/hooks/api";
import { apiUrls } from "../../actprep-ui/utils/urls";
import { useCallback, useEffect, useState } from "react";
import { answerChoiceOptions } from "../../actprep-ui/constants/testing";
import { capitalize } from "../../actprep-ui/utils/formatting";
import ModelAutoComplete from "../../actprep-ui/components/ModelAutoComplete";
import VideoForm from "../VideoForm";
import useDisclosure from "../../actprep-ui/hooks/ui/useDisclosure";
import ImageDialog from "../../actprep-ui/components/ImageDialog";
import TestQuestionImageForm from "../TestQuestionImageForm";
import { Add, Delete, Edit, Visibility } from "@mui/icons-material";
import { showSuccessSnackbar } from "../../actprep-ui/utils/feedback";
import api from "../../actprep-ui/utils/api";

export default function TestQuestionForm({
  testQuestionId,
  subject,
  test,
  onSubmit,
}) {
  const {
    data,
    refetch,
    isLoading: isLoadingTestQuestion,
  } = useGet(apiUrls.admin.testQuestions(testQuestionId), {
    enabled: !!testQuestionId,
    params: {
      keep_related: true,
    },
    staleTime: 0,
  });
  const {
    data: imageData,
    refetch: refetchImageData,
    isLoading: isLoadingImages,
  } = useGet(apiUrls.admin.testQuestionImages(), {
    params: {
      search_params: {
        test_question_id: testQuestionId,
      },
      enabled: !!testQuestionId,
    },
    staleTime: 0,
  });

  const isLoading = isLoadingTestQuestion || isLoadingImages;

  const isEditing = !!testQuestionId;

  const [formData, setFormData] = useState({
    question_number: "",
    correct_answer: "",
    category: null,
    video: null,
    has_diagram: false,
    is_field_test_question: false,
    answer_choices: [],
  });

  // Image management state
  const [images, setImages] = useState([]); // Each image has: uuid, image, image_type, has_diagram, file (optional), action (optional: 'DELETE', 'UPDATE')
  const [editingImage, setEditingImage] = useState(null);
  const [viewingImage, setViewingImage] = useState(null);
  const [error, setError] = useState(null);
  const [imageFormOpen, setImageFormOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const validateForm = useCallback(() => {
    const errors = {};
    if (parseInt(formData.question_number ?? 0) <= 0) {
      errors.question_number = "Question number must be greater than 0";
    }

    // Answer choices validation
    if (formData.answer_choices.length < 2) {
      errors.answer_choices = "At least 2 choices are required";
    } else if (formData.answer_choices.length > 5) {
      errors.answer_choices = "At most 5 choices are allowed";
    }

    // Correct answer validation
    if (!formData.answer_choices?.includes(formData.correct_answer)) {
      errors.correct_answer = "Correct answer must be one of the choices";
    }
    setValidationErrors(errors);
  }, [formData]);

  useEffect(() => {
    validateForm();
  }, [formData, validateForm]);

  const viewImageDisclosure = useDisclosure({
    onClose: () => setViewingImage(null),
  });

  useEffect(() => {
    if (data) {
      const testQuestion = data.data;
      setFormData({
        question_number: testQuestion.question_number,
        correct_answer: testQuestion.correct_answer,
        category: testQuestion.category
          ? {
              label: testQuestion.category.name,
              uuid: testQuestion.category.uuid,
            }
          : null,
        video: testQuestion.video
          ? {
              label: testQuestion.video.title,
              uuid: testQuestion.video.uuid,
            }
          : null,
        has_diagram: testQuestion.has_diagram,
        is_field_test_question: testQuestion.is_field_test_question,
        answer_choices: testQuestion.answer_choices || [],
      });
    }
  }, [data]);

  // Sync test question images to local state
  useEffect(() => {
    if (imageData?.data) {
      setImages(imageData.data);
    }
  }, [imageData]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Create FormData for multipart/form-data submission
    const payload = new FormData();

    // Add basic form fields
    payload.append("question_number", formData.question_number);
    payload.append("correct_answer", formData.correct_answer);
    payload.append("subject", subject);
    payload.append("test_id", test.uuid);

    if (formData.category) {
      payload.append("category_id", formData.category.uuid);
    }

    if (formData.video) {
      payload.append("video_id", formData.video.uuid);
    }

    payload.append("has_diagram", formData.has_diagram);
    payload.append("is_field_test_question", formData.is_field_test_question);
    payload.append("answer_choices", formData.answer_choices.join(","));

    // Prepare unified image list with actions
    const imagesMetadata = [];
    let fileIndex = 0;

    images.forEach((image) => {
      const metadata = {
        image_type: image.image_type,
        has_diagram: image.has_diagram,
      };

      if (image.file) {
        // New image - add file and reference it by index
        payload.append("image_files", image.file);
        metadata.file_index = fileIndex;
        metadata.action = "CREATE";
        fileIndex++;
      } else if (image.uuid) {
        // Existing image
        metadata.uuid = image.uuid;

        // Add action if present (UPDATE or DELETE)
        if (image.action) {
          metadata.action = image.action;
        }
        // If no action, backend will treat as unchanged
      }

      imagesMetadata.push(metadata);
    });

    // Add metadata as JSON
    if (imagesMetadata.length > 0) {
      payload.append("images", JSON.stringify(imagesMetadata));
    }

    const url = isEditing
      ? apiUrls.admin.testQuestions(testQuestionId)
      : apiUrls.admin.testQuestions();
    const response = await api.post(url, payload, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    if (response.status != 200) {
      setError(
        `${isEditing ? "Failed to update" : "Failed to create"} question`
      );
      return;
    }
    const { success, reason } = response.data;
    if (!success) {
      setError(
        `${
          isEditing ? "Failed to update" : "Failed to create"
        } question: ${reason}`
      );
      return;
    }
    showSuccessSnackbar(
      isEditing
        ? "Question updated successfully!"
        : "Question created successfully!"
    );
    refetch();
    refetchImageData();
    onSubmit(response.data.data);
  };

  const handleFormChangeGeneric = (field) => (event) => {
    setFormData((formData) => ({ ...formData, [field]: event.target.value }));
  };
  const handleFormChangeSwitch = (field) => (_, checked) => {
    setFormData((formData) => ({ ...formData, [field]: checked }));
  };
  const handleModelAutocompleteChange = (field) => (newValue) => {
    setFormData((formData) => ({ ...formData, [field]: newValue }));
  };

  // Image CRUD handlers
  const handleViewImage = (image) => {
    setViewingImage(image);
    viewImageDisclosure.onOpen();
  };

  const handleEditImage = (image) => {
    setEditingImage(image);
    setImageFormOpen(true);
  };

  const handleAddImage = () => {
    setEditingImage(null);
    setImageFormOpen(true);
  };

  const handleDeleteImage = (imageId) => {
    if (!imageId) {
      // New image (no uuid), just remove from array
      setImages((prev) => prev.filter((img) => img.uuid !== imageId));
    } else {
      // Existing image - mark for deletion
      setImages((prev) =>
        prev.map((img) =>
          img.uuid === imageId ? { ...img, action: "DELETE" } : img
        )
      );
    }
  };

  const handleSaveImage = (imageData) => {
    if (editingImage) {
      // Editing existing image
      setImages((prev) =>
        prev.map((img) =>
          img.uuid === editingImage.uuid
            ? {
                ...img,
                image_type: imageData.image_type,
                has_diagram: imageData.has_diagram,
                action: "UPDATE",
              }
            : img
        )
      );
    } else {
      // Adding new image
      const newImage = {
        uuid: null,
        image: imageData.preview,
        has_diagram: imageData.has_diagram,
        image_type: imageData.image_type,
        file: imageData.file,
      };
      setImages((prev) => [...prev, newImage]);
    }
  };

  return (
    <Box
      component={"form"}
      onSubmit={handleSubmit}
      sx={{ position: "relative" }}
    >
      {/* Loading Overlay */}
      {isLoading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            zIndex: 1000,
            borderRadius: 1,
          }}
        >
          <CircularProgress />
        </Box>
      )}

      <Grid
        container
        spacing={3}
        mt={1}
        sx={{
          width: "100%",
          opacity: isLoading ? 0.5 : 1,
          pointerEvents: isLoading ? "none" : "auto",
        }}
      >
        <Grid size={12}>
          <Typography variant="body1" fontWeight={"bold"}>
            {isEditing ? "Editing" : "Adding"} {test.name} {capitalize(subject)}{" "}
            {formData.question_number
              ? `Question #${formData.question_number}`
              : "New Question"}
          </Typography>
        </Grid>
        <Grid size={6}>
          <TextField
            fullWidth
            value={formData.question_number}
            onChange={handleFormChangeGeneric("question_number")}
            label="Question Number"
            variant="standard"
            slotProps={{
              input: {
                type: "number",
              },
            }}
            required
            error={!!validationErrors.question_number}
          />
          <FormHelperText error={!!validationErrors.question_number}>
            {validationErrors.question_number}
          </FormHelperText>
        </Grid>
        <Grid size={6}>
          <FormControl fullWidth variant="standard" required>
            <InputLabel>Correct Answer</InputLabel>
            <Select
              value={formData.correct_answer}
              onChange={handleFormChangeGeneric("correct_answer")}
              label="Correct Answer"
              MenuProps={{
                sx: (theme) => ({
                  zIndex: theme.zIndex.modal + 2,
                }),
              }}
            >
              {answerChoiceOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText error={!!validationErrors.correct_answer}>
              {validationErrors.correct_answer}
            </FormHelperText>
          </FormControl>
        </Grid>
        <Grid size={12}>
          <ModelAutoComplete
            modelName="Category"
            label="Category"
            value={formData.category}
            setValue={handleModelAutocompleteChange("category")}
            slotProps={{
              popper: {
                sx: (theme) => ({
                  zIndex: theme.zIndex.modal + 2,
                }),
              },
            }}
            defaultParams={{
              subject: subject,
            }}
          />
        </Grid>
        <Grid size={12}>
          <ModelAutoComplete
            modelName={"Video"}
            label={"Video"}
            value={formData.video}
            setValue={handleModelAutocompleteChange("video")}
            allowCreation
            CreationForm={(props) => (
              <VideoForm
                {...props}
                DialogProps={{
                  slotProps: {
                    root: {
                      sx: (theme) => ({
                        zIndex: theme.zIndex.modal + 2,
                      }),
                    },
                  },
                }}
              />
            )}
            slotProps={{
              popper: {
                sx: (theme) => ({
                  zIndex: theme.zIndex.modal + 2,
                }),
              },
            }}
          />
        </Grid>
        <Grid size={12}>
          <FormControl fullWidth variant="standard">
            <InputLabel>Choices</InputLabel>
            <Select
              multiple
              label="Choices"
              value={formData.answer_choices}
              onChange={handleFormChangeGeneric("answer_choices")}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
              MenuProps={{
                sx: (theme) => ({
                  zIndex: theme.zIndex.modal + 2,
                }),
              }}
            >
              {answerChoiceOptions.map((answerChoice) => (
                <MenuItem
                  key={answerChoice}
                  value={answerChoice}
                  dense
                  disableGutters
                >
                  <Checkbox
                    checked={formData.answer_choices.includes(answerChoice)}
                  />
                  <ListItemText primary={answerChoice} />
                </MenuItem>
              ))}
            </Select>
            <FormHelperText error={!!validationErrors.answer_choices}>
              {validationErrors.answer_choices}
            </FormHelperText>
          </FormControl>
        </Grid>
        <Grid size={12}>
          <FormControlLabel
            label="Field Test Question"
            control={
              <Switch
                checked={formData.is_field_test_question}
                onChange={handleFormChangeSwitch("is_field_test_question")}
              />
            }
          />
        </Grid>
        <Grid size={12}>
          <Typography variant="h6" fontWeight={"bold"} mt={2}>
            Images
          </Typography>
        </Grid>
        <Grid size={12}>
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={handleAddImage}
            fullWidth
            size="small"
          >
            Add Image
          </Button>
        </Grid>
        {images
          .filter((image) => image.action !== "DELETE")
          .map((image, index) => (
            <Grid size={12} key={image.uuid || `new-${index}`}>
              <Card>
                <CardMedia
                  component="img"
                  image={image.image}
                  alt={`Question image ${index + 1}`}
                  sx={{ objectFit: "contain", cursor: "pointer" }}
                  onClick={() => handleViewImage(image)}
                />
                <CardContent>
                  <Stack direction="row" spacing={1}>
                    <Chip
                      label={image.image_type}
                      color={
                        image.image_type === "Primary" ? "primary" : "secondary"
                      }
                      size="small"
                    />
                    {image.has_diagram && (
                      <Chip label="Has Diagram" size="small" />
                    )}
                    {image.file && (
                      <Chip label="New" color="success" size="small" />
                    )}
                    {image.action === "UPDATE" && (
                      <Chip label="Modified" color="warning" size="small" />
                    )}
                  </Stack>
                </CardContent>
                <CardActions>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleViewImage(image)}
                    title="View Image"
                  >
                    <Visibility />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleEditImage(image)}
                    title="Edit Image"
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteImage(image.uuid)}
                    title="Delete Image"
                  >
                    <Delete />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}

        <Grid size={12}>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            sx={{ mt: 3 }}
            disabled={Object.values(validationErrors).length > 0}
          >
            {isEditing ? "Save Changes" : "Create Question"}
          </Button>
        </Grid>
        <Grid size={12}>
          {error && (
            <Alert
              severity="error"
              sx={{ mt: 2 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}
        </Grid>
      </Grid>

      {/* View Image Dialog */}
      <ImageDialog
        src={viewingImage?.image}
        {...viewImageDisclosure.getDisclosureProps()}
        sx={(theme) => ({
          zIndex: theme.zIndex.modal + 2,
        })}
      />

      {/* Add/Edit Image Form */}
      <TestQuestionImageForm
        open={imageFormOpen}
        onClose={() => {
          setImageFormOpen(false);
          setEditingImage(null);
        }}
        onSave={handleSaveImage}
        existingImage={editingImage}
        allImages={images}
      />
    </Box>
  );
}
